import io from "socket.io-client"
import {
  deleteCacheByKey,
  patchCacheItem,
  db,
} from "../../view/shared/utilities/cache"
import { triggerAction } from "../constants"
import { fetchVslas } from "../actions/getVslas"
import {
  fetchActiveMeetingTransactions,
} from "../actions/meetings"
import { fetchVslasMembers } from "../actions/members/getVslaMembers"
import { EXPENSE_TRANSACTION, getMemberBalanceByMemberId, INCOME_TRANSACTION } from "../reducers/transactions/actions"

let refreshTimeout
export const debounce = (func, delay) => {
  return function (...args) {
    clearTimeout(refreshTimeout)
    refreshTimeout = setTimeout(() => func.apply(this, args), delay)
  }
}

const eventHandlers = new Map()

const middlewareFunction = () => {
  const isBrowser = typeof window !== "undefined"
  let socket

  return ({ dispatch, getState }) => {
    const debouncedRefresh = debounce(() => {
      const meetingId = getState().meeting?.ongoing?.id
      if (meetingId) {
        dispatch(fetchActiveMeetingTransactions(meetingId, false))
      }
      dispatch(fetchVslas())
    }, 2000) // 2-second debounce window

    if (!socket && isBrowser) {
      socket = io.connect(window.REACT_APP_WS_URL, {
        transports: ["websocket"],
      })

      socket.on("connect", () => {
        console.log("[WS] Socket connected successfully.")
        const userId = getState().user?.id
        if (userId) {
          socket.emit("reconnect_user", { userId })
        }
        eventHandlers.forEach((handler, eventName) => {
          socket.on(eventName, handler)
        })
      })

      socket.on("connected", () => {
        console.log("[WS] Socket connected successfully.")
      })

      socket.on("disconnect", (reason) => {
        console.log(`[WS] Socket disconnected: ${JSON.stringify(reason)}`)
        eventHandlers.forEach((handler, eventName) => {
          socket.off(eventName, handler)
        })
      })

      socket.on("connect_error", (error) => {
        console.error("[WS] Connection Error:", error)
      })
    }

    return (next) => (action) => {
      if (typeof action === "function" || !isBrowser) {
        return next(action)
      }
      if (!action) {
        return
      }
      console.log("[MW] Action received:", action.type)

      const { pushEvent, receiveEvent, leave, payload, emit } = action

      if (!pushEvent) {
        return next(action)
      }

      console.log(`[WS] Attaching listener for event: "${receiveEvent}"`)

      if (leave) {
        socket.removeListener(pushEvent)
      }

      if (emit) {
        socket.emit(pushEvent, payload)
      }

      const isTransactionResposne = async (requestId) => {
        const cachedRequest = await db.table('offlineRequests').where({
          tempId: requestId
        }).first();
        return (cachedRequest && (
          INCOME_TRANSACTION.includes(cachedRequest.entityType) ||
          EXPENSE_TRANSACTION.includes(cachedRequest.entityType)) ||
          action.metadata?.type === 'transactions')
      }
      const handleReceivedData = async (data) => {
        const payload = Array.isArray(data) ? data[1] : data
        const cacheId = payload?.data?.requestId || payload?.requestId
        const type = action.metadata?.type
        socket.emit("ack", {
          userId: payload.userId,
          messageId: payload.messageId,
        })
        const isTransaction = await isTransactionResposne(cacheId)

        try {
          if (payload.processStatus === "FAILED") {
            dispatch(triggerAction("TOGGLE_FAILED_TO_UPLOAD", true))
            patchCacheItem("offlineRequests", cacheId, {
              error: payload.data.description,
              status: "error",
            })
            if (type === "members") {
              dispatch({
                type: "CREATE_MEMBER_ERROR",
                payload: { tempId: cacheId, error: payload.data.msg },
              })
            }
          } else if (payload.processStatus === "PENDING") {
            if (type === "members") {
              dispatch({
                type: "UPDATE_MEMBER_STATUS",
                payload: {
                  tempId: cacheId,
                  syncStatus: "processing",
                  error: null,
                },
              })
            }
            else if (isTransaction || type === "transactions") {
              debouncedRefresh()
              await deleteCacheByKey("offlineRequests", "cacheId", cacheId)
              dispatch(triggerAction("REMOVE_TRANSACTION_BY_REQUEST_ID", { requestId: cacheId }))
              setTimeout(() => {
                // refresh after 3 seconds to allow backend to process
                // workaround for the websocket bug where connection is not stble
                debouncedRefresh()
              }, 3000);
            }
          } else if (payload.processStatus === "SUCCESSFUL") {
            if (type === "members") {
              dispatch(fetchVslasMembers()) // reload the members
            }
            else if (isTransaction) {
              dispatch(getMemberBalanceByMemberId(payload.data.initiator))
              debouncedRefresh() // fetch the updated transactions for the meeting
            }
            await deleteCacheByKey("offlineRequests", "cacheId", cacheId)
            dispatch(triggerAction("REMOVE_TRANSACTION_BY_REQUEST_ID", { requestId: cacheId }))
          }
        } catch (error) {
          console.log("[Info] error", error)
        }
      }

      if (eventHandlers.has(receiveEvent)) {
        socket.off(receiveEvent, eventHandlers.get(receiveEvent))
      }
      eventHandlers.set(receiveEvent, handleReceivedData)
      socket.on(receiveEvent, handleReceivedData)

      return next(action)
    }
  }
}

export default middlewareFunction
